// services/backtestService.js
import axios from 'axios';

// ✅ Use environment variable instead of hardcoded localhost
const API_BASE = process.env.REACT_APP_API_BASEURL || 'http://localhost:3005';
const API = `${API_BASE}/api/backtest`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: token } : {};
};

// Helper to get userId from localStorage
const getUserId = () => {
  const userId = localStorage.getItem('userId');
  return userId ? parseInt(userId, 10) : null;
};

// ✅ Submit backtest - includes userId automatically
const submit = async (params) => {
  const userId = getUserId();
  const res = await axios.post(API, { ...params, userId }, {
    headers: getAuthHeaders()
  });
  return res.data;
};

// ✅ Save backtest result
async function saveResult(payload) {
  const userId = getUserId();
  const { data } = await axios.post(`${API}/save`, { ...payload, userId }, {
    headers: getAuthHeaders()
  });
  return data;
}

// ✅ GET /api/backtest/status/:jobId
const getStatus = async (jobId) => {
  const res = await axios.get(`${API}/status/${jobId}`, {
    headers: getAuthHeaders()
  });
  return res.data;
};

// ✅ GET /api/backtest/recent - auto-includes userId
const getResults = async (limit = 10) => {
  const userId = getUserId();
  const res = await axios.get(`${API}/recent`, { 
    params: { userId, limit },
    headers: getAuthHeaders()
  });
  return res.data;
};

// ✅ GET /api/backtest/:id
const getDetail = async (id) => {
  const res = await axios.get(`${API}/${id}`, {
    headers: getAuthHeaders()
  });
  return res.data;
};

// ✅ DELETE /api/backtest/:id
const deleteResult = async (id) => {
  const userId = getUserId();
  const res = await axios.delete(`${API}/${id}`, {
    params: { userId },
    headers: getAuthHeaders()
  });
  return res.data; // { ok: true, id }
};

// ✅ GET /api/backtest/active - auto-includes userId
const getActive = async () => {
  const userId = getUserId();
  const res = await axios.get(`${API}/active`, { 
    params: { userId },
    headers: getAuthHeaders()
  });
  return res.data; // {active, jobId, status, progress}
};

// ✅ Cancel backtest
const cancel = async (jobId) => {
  if (!jobId) throw new Error('Missing jobId');
  try {
    const res = await axios.post(`${API}/cancel/${encodeURIComponent(jobId)}`, {}, {
      headers: getAuthHeaders()
    });
    return res.data;
  } catch (err) {
    // Fallback for servers that use a JSON body instead of a URL param
    if (err?.response?.status === 404) {
      const res2 = await axios.post(`${API}/cancel`, { jobId }, {
        headers: getAuthHeaders()
      });
      return res2.data;
    }
    throw err;
  }
};


// ── Feature scan ──
// Does a condition predict anything, before spending a backtest on it? Async:
// POST returns a scanId, the result is polled.
const startFeatureScan = async (payload) => {
  const res = await axios.post(`${API}/feature-scan`, { userId: getUserId(), ...payload }, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.data;
};

const getFeatureScan = async (scanId) => {
  const res = await axios.get(`${API}/feature-scan/${encodeURIComponent(scanId)}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};


// ── Parameter sweep ──
// Vary the numbers in a strategy and report the SHAPE of the response.
//
// planSweep is synchronous and runs nothing — it returns the axes the server
// considers tunable plus a combination count. The UI renders exactly what comes
// back rather than guessing which numbers are sweepable, so what is offered and
// what the runner rewrites cannot drift apart.
const planSweep = async (payload) => {
  const res = await axios.post(`${API}/sweep/plan`, { userId: getUserId(), ...payload }, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.data;
};

// The sweep itself is ONE job that runs the whole grid; POST returns a sweepId.
// A 400 here is meaningful (over the combination cap) and carries the count.
const startSweep = async (payload) => {
  const res = await axios.post(`${API}/sweep`, { userId: getUserId(), ...payload }, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.data;
};

const getSweep = async (sweepId) => {
  const res = await axios.get(`${API}/sweep/${encodeURIComponent(sweepId)}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};


// ── Multi-symbol batch ──
// One strategy, many symbols. Async: POST returns a searchId, result is polled.
const startBatch = async (payload) => {
  const res = await axios.post(`${API}/batch`, { userId: getUserId(), ...payload }, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.data;
};

const cancelBatch = async (searchId) => {
  const res = await axios.post(`${API}/batch/${encodeURIComponent(searchId)}/cancel`, {}, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.data;
};

const getBatch = async (searchId) => {
  const res = await axios.get(`${API}/batch/${encodeURIComponent(searchId)}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};

// ── Correlation between saved runs ──
// Synchronous — it reads stored equity curves, there is nothing to simulate.
const getCorrelation = async ({ limit = 20, threshold = 0.3 } = {}) => {
  const res = await axios.get(`${API}/correlation`, {
    params: { userId: getUserId(), limit, threshold },
    headers: getAuthHeaders(),
  });
  return res.data;
};


// ── Symbol universe ──
// Lives under /api/symbols but is called from the backtest research panels, so
// it sits here where the correct base URL and auth headers already are.
// symbolService.js defaults to port 3000, which is not where the backend runs.
// The named destructure here used to drop every param it did not list, so the
// picker's category, shortable and exchange filters were built by the caller and
// then silently discarded — this path returned the whole venue whatever was
// asked for. Forward what the caller sent instead, minus the blanks.
//
// `quote` in particular must NOT default: the caller omits it deliberately to
// mean "all quotes" (or "equity venue, no such concept"), and a 'USDT' default
// turned both of those into a USDT-only filter.
const getSymbolUniverse = async (opts = {}) => {
  const { limit = 20, rank = 'volume', feeFilter = 'any', ...rest } = opts;
  const params = { limit, rank, feeFilter, ...rest };
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined || params[k] === null || params[k] === '') delete params[k];
  });
  const res = await axios.get(`${API_BASE}/api/symbols/universe`, {
    params,
    headers: getAuthHeaders(),
  });
  return res.data;
};


// ── Funding carry research ──
// Perp funding as an edge source rather than another indicator: a scheduled
// payment rather than a price prediction, which is why it pays for holding
// instead of for trading.
const getFundingResearch = async ({ symbols, source = 'mexc' } = {}) => {
  const res = await axios.get(`${API_BASE}/api/research/funding`, {
    params: { symbols: (symbols || []).join(','), source },
    headers: getAuthHeaders(),
  });
  return res.data;
};

// ── Deployment gate ──
// Read-only evaluation of whether a strategy has earned live capital.
const getDeploymentGate = async ({ botId, buy, sell } = {}) => {
  const res = await axios.get(`${API_BASE}/api/backtest/deployment-gate`, {
    params: { userId: getUserId(), botId, buy, sell },
    headers: getAuthHeaders(),
  });
  return res.data;
};


// ── Validation funnel ──
// signal -> backtest -> breadth -> gate, stopping at the first failure.
const startValidation = async (payload) => {
  const res = await axios.post(`${API_BASE}/api/backtest/validate`,
    { userId: getUserId(), ...payload },
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } });
  return res.data;
};

const getValidation = async (id) => {
  const res = await axios.get(`${API_BASE}/api/backtest/validate/${encodeURIComponent(id)}`,
    { headers: getAuthHeaders() });
  return res.data;
};

export default {
  submit,
  getStatus,
  saveResult,
  getResults,
  getFundingResearch,
  getDetail,
  deleteResult,
  cancel,
  getActive,
  startFeatureScan,
  getFeatureScan,
  planSweep,
  startSweep,
  getSweep,
  startBatch,
  cancelBatch,
  getBatch,
  getCorrelation,
  getSymbolUniverse,
  getDeploymentGate,
  startValidation,
  getValidation
};